import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Settings,
    AlertTriangle,
    CheckCircle,
    Clock,
    Loader2,
    Activity,
    TrendingUp,
    TrendingDown,
} from 'lucide-react';
import { useOpenPositions } from '@/api/positions/use-positions';
import { useTradingAccounts } from '@/api/trading-accounts/use-crm-trading-accounts';
import { useNavigate } from 'react-router-dom';

interface AutomationStatusWidgetProps {
    tradingAccountId?: string;
    className?: string;
    showActions?: boolean;
}

const AutomationStatusWidget: React.FC<AutomationStatusWidgetProps> = ({
    tradingAccountId,
    className,
    showActions = true,
}) => {
    const navigate = useNavigate();

    const { data: positionsData, isLoading: isPositionsLoading } =
        useOpenPositions(tradingAccountId);
    const { data: accountsData, isLoading: isAccountsLoading } = useTradingAccounts();

    const positions = positionsData?.data || [];
    const accounts = accountsData?.data || [];
    const selectedAccount = accounts.find(acc => acc.id === tradingAccountId);

    const isLoading = isPositionsLoading || isAccountsLoading;

    // Calculate automation metrics
    const automationMetrics = {
        totalPositions: positions.length,
        positionsWithSL: positions.filter(pos => pos.stop_loss_price).length,
        positionsWithTP: positions.filter(pos => pos.take_profit_price).length,
        positionsNearSL: positions.filter(pos => {
            if (!pos.stop_loss_price || !pos.current_price) return false;
            const distance = Math.abs(pos.current_price - pos.stop_loss_price) / pos.current_price;
            return distance < 0.02; // Within 2% of stop loss
        }).length,
        positionsNearTP: positions.filter(pos => {
            if (!pos.take_profit_price || !pos.current_price) return false;
            const distance =
                Math.abs(pos.current_price - pos.take_profit_price) / pos.current_price;
            return distance < 0.02; // Within 2% of take profit
        }).length,
        marginCallRisk: selectedAccount ? selectedAccount.margin_status?.margin_call : false,
        liquidationRisk: selectedAccount
            ? selectedAccount.margin_status?.liquidation_warning
            : false,
        marginLevel: selectedAccount ? selectedAccount.margin_level : 0,
    };

    const getAutomationStatus = () => {
        if (automationMetrics.liquidationRisk)
            return { status: 'critical', color: 'red', label: 'Liquidation Risk' };
        if (automationMetrics.marginCallRisk)
            return { status: 'warning', color: 'orange', label: 'Margin Call' };
        if (automationMetrics.positionsNearSL > 0 || automationMetrics.positionsNearTP > 0) {
            return { status: 'monitoring', color: 'yellow', label: 'Monitoring' };
        }
        return { status: 'active', color: 'green', label: 'Active' };
    };

    const automationStatus = getAutomationStatus();

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'critical':
                return 'text-red-600 bg-red-50 dark:bg-red-950/20';
            case 'warning':
                return 'text-orange-600 bg-orange-50 dark:bg-orange-950/20';
            case 'monitoring':
                return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950/20';
            case 'active':
                return 'text-green-600 bg-green-50 dark:bg-green-950/20';
            default:
                return 'text-gray-600 bg-gray-50 dark:bg-gray-950/20';
        }
    };

    const formatNumber = (num: number, decimals: number = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(num);
    };

    const handleViewPositions = () => {
        navigate('/trade');
    };

    const handleViewSettings = () => {
        navigate('/profile');
    };

    if (!tradingAccountId) {
        return (
            <Card className={className}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Automation Status
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center text-muted-foreground py-8">
                        <Settings className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                            Select a trading account to view automation status
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Automation Status
                    </CardTitle>
                    {showActions && (
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={handleViewSettings}>
                                <Settings className="h-4 w-4 mr-2" />
                                Settings
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleViewPositions}>
                                <Activity className="h-4 w-4 mr-2" />
                                Positions
                            </Button>
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Overall Status */}
                        <div
                            className={`p-4 rounded-lg ${getStatusColor(automationStatus.status)}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {automationStatus.status === 'critical' && (
                                        <AlertTriangle className="h-5 w-5" />
                                    )}
                                    {automationStatus.status === 'warning' && (
                                        <AlertTriangle className="h-5 w-5" />
                                    )}
                                    {automationStatus.status === 'monitoring' && (
                                        <Clock className="h-5 w-5" />
                                    )}
                                    {automationStatus.status === 'active' && (
                                        <CheckCircle className="h-5 w-5" />
                                    )}
                                    <span className="font-semibold">{automationStatus.label}</span>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    {automationMetrics.totalPositions} Positions
                                </Badge>
                            </div>
                        </div>

                        {/* Risk Indicators */}
                        {(automationMetrics.marginCallRisk ||
                            automationMetrics.liquidationRisk) && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Risk Alerts
                                </h4>
                                <div className="space-y-2">
                                    {automationMetrics.liquidationRisk && (
                                        <div className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                                <span className="text-sm font-medium text-red-700 dark:text-red-300">
                                                    Liquidation Risk
                                                </span>
                                            </div>
                                            <Badge variant="destructive" className="text-xs">
                                                Critical
                                            </Badge>
                                        </div>
                                    )}
                                    {automationMetrics.marginCallRisk &&
                                        !automationMetrics.liquidationRisk && (
                                            <div className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                                                <div className="flex items-center gap-2">
                                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                                                        Margin Call
                                                    </span>
                                                </div>
                                                <Badge
                                                    variant="secondary"
                                                    className="text-xs bg-orange-100 text-orange-800"
                                                >
                                                    Warning
                                                </Badge>
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}

                        {/* Margin Level */}
                        {selectedAccount && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Margin Level
                                </h4>
                                <div className="text-center p-3 bg-muted rounded-lg">
                                    <div
                                        className={`text-2xl font-bold ${
                                            automationMetrics.marginLevel > 200
                                                ? 'text-green-600'
                                                : automationMetrics.marginLevel > 100
                                                  ? 'text-yellow-600'
                                                  : 'text-red-600'
                                        }`}
                                    >
                                        {formatNumber(automationMetrics.marginLevel, 1)}%
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {automationMetrics.marginLevel > 200
                                            ? 'Safe'
                                            : automationMetrics.marginLevel > 100
                                              ? 'Caution'
                                              : 'Risk'}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Position Protection */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-muted-foreground">
                                Position Protection
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="text-center p-2 bg-muted rounded-lg">
                                    <div className="text-sm font-bold">
                                        {automationMetrics.positionsWithSL}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Stop Loss</div>
                                </div>
                                <div className="text-center p-2 bg-muted rounded-lg">
                                    <div className="text-sm font-bold">
                                        {automationMetrics.positionsWithTP}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Take Profit</div>
                                </div>
                            </div>
                        </div>

                        {/* Monitoring Alerts */}
                        {(automationMetrics.positionsNearSL > 0 ||
                            automationMetrics.positionsNearTP > 0) && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Monitoring
                                </h4>
                                <div className="space-y-2">
                                    {automationMetrics.positionsNearSL > 0 && (
                                        <div className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                            <div className="flex items-center gap-2">
                                                <TrendingDown className="h-4 w-4 text-yellow-500" />
                                                <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                                                    Near Stop Loss
                                                </span>
                                            </div>
                                            <Badge
                                                variant="secondary"
                                                className="text-xs bg-yellow-100 text-yellow-800"
                                            >
                                                {automationMetrics.positionsNearSL}
                                            </Badge>
                                        </div>
                                    )}
                                    {automationMetrics.positionsNearTP > 0 && (
                                        <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                                            <div className="flex items-center gap-2">
                                                <TrendingUp className="h-4 w-4 text-green-500" />
                                                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                                                    Near Take Profit
                                                </span>
                                            </div>
                                            <Badge
                                                variant="secondary"
                                                className="text-xs bg-green-100 text-green-800"
                                            >
                                                {automationMetrics.positionsNearTP}
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Protection Coverage */}
                        {automationMetrics.totalPositions > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Protection Coverage
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Stop Loss Coverage</span>
                                        <span className="font-medium">
                                            {Math.round(
                                                (automationMetrics.positionsWithSL /
                                                    automationMetrics.totalPositions) *
                                                    100
                                            )}
                                            %
                                        </span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${(automationMetrics.positionsWithSL / automationMetrics.totalPositions) * 100}%`,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Take Profit Coverage</span>
                                        <span className="font-medium">
                                            {Math.round(
                                                (automationMetrics.positionsWithTP /
                                                    automationMetrics.totalPositions) *
                                                    100
                                            )}
                                            %
                                        </span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${(automationMetrics.positionsWithTP / automationMetrics.totalPositions) * 100}%`,
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default AutomationStatusWidget;
